<template>
    <Modal
        ref="modalRef"
        :primary-button-disabled="
            !asset ||
            !form.isDirty ||
            uploadInProgress ||
            isRequiredPropertyMissing ||
            (inoperableReminderVisible && !form.metadata.inoperability_tag_acknowledged)
        "
        :primary-button-label="trans('general.action.create')"
        :processing="form.processing"
        max-width="2xl"
        @submit="submitForm"
    >
        <template #title>
            <div class="-mx-8 -my-4 bg-[#002377] px-8 py-4 text-white">
                <asset-short-info v-if="asset" :asset="asset" :clickable="true" @asset-details-visited="modalRef?.close()">
                    <template #top>
                        <status-badge :enable-mobile="true" :operability="asset.status.operability" />
                    </template>
                </asset-short-info>
                <div v-else>{{ trans('workorder.modal.create.title') }}</div>
            </div>
        </template>

        <asset-selection-field
            v-if="showAssetSearch"
            ref="assetSelectionRef"
            :error="$page.props.errors.assetId"
            :placeholder="trans('workorder.modal.create.asset_selection.placeholder')"
            :processing="form.processing"
            :required="true"
            :search-url="route('workorders.search-assets')"
            class="mb-8"
            @selected="loadAssetDetails"
        />

        <div class="mb-11 flex items-center justify-between gap-3">
            <div class="flex min-w-0 items-center gap-3">
                <work-order-tag />
            </div>
            <div v-if="priorityEnabled" class="flex items-center gap-3">
                <jet-select
                    v-model="form.priority"
                    :disabled="(priorities?.data || []).length === 0"
                    :horizontal="false"
                    :inline="true"
                    :invalid-feedback="form.errors.priority"
                    :options="priorities?.data || []"
                    :readonly="priorities.length === 0"
                    :required="true"
                    class="!mb-0 h-8 rounded-[8px] border-1 px-2 py-1"
                >
                    <template #readonly>
                        <span v-if="workOrder">
                            {{ workOrder?.priority.label }}
                        </span>
                        <span v-else>{{ trans('general.none') }}</span>
                    </template>
                </jet-select>
            </div>
        </div>

        <inoperability-reminder-checkbox
            v-if="inoperableReminderVisible"
            v-model="form.metadata.inoperability_tag_acknowledged"
            :reminder-key="inoperableReminderKey"
            id="inoperability-tag-acknowledged-create"
            class="-mt-7 mb-4 !ps-1"
        />

        <collapsable :is-collapsable="false" class="mb-7">
            <template #toggler>
                <h3 class="mb-1 !leading-[22px] !font-bold">
                    <inline-editable-field
                        ref="titleRef"
                        v-model="form.title"
                        :invalid-feedback="form.errors.title"
                        :line-height="24"
                        :placeholder="trans('workorder.form.title.placeholder')"
                    />
                </h3>
            </template>

            <inline-editable-field
                v-model="form.description"
                :invalid-feedback="form.errors.description"
                :placeholder="trans('workorder.form.description.placeholder')"
                :rows="2"
                class="mb-4"
                name="description"
            />

            <case-category-section
                v-if="catsaExtrasEnabled && asset"
                v-model:category="form.metadata.case_category"
                v-model:subcategory="form.metadata.case_subcategory"
                :asset-type="asset?.type?.value"
                :categories="caseCategories"
                :category-readonly="true"
                class="mb-7"
            />

            <troubleshooting-section v-if="troubleshootingEnabled" v-model="form.metadata.troubleshooting" class="mb-7" />

            <msd-notification-section v-if="msdNotificationEnabled" v-model="form.metadata.msd_notification" class="mb-7" />

            <impact-assessment-section v-if="impactAssessmentEnabled" v-model="form.metadata.impact_assessment" class="mb-7" />

            <work-order-info :assignees="assignableGroups" :form="form" :types="types?.data || []" :catsa-extras="catsaExtras" />

            <dl class="mb-0">
                <div class="row">
                    <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                        {{ trans(catsaExtrasEnabled ? 'workorder.details.workorder.case_id.label' : 'workorder.details.workorder.ticket_id.label') }}
                    </dt>
                    <dd class="col-sm-8 col-12 text-muted">
                        <inline-editable-field
                            v-model="form.ticketId"
                            :invalid-feedback="form.errors.ticketId"
                            :line-height="22"
                            :placeholder="trans(catsaExtrasEnabled ? 'workorder.form.case_id.placeholder' : 'workorder.form.ticket_id.placeholder')"
                            :resizable="false"
                        />
                    </dd>
                </div>
                <div class="row">
                    <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                        {{ trans('workorder.details.workorder.ticket_link.label') }}
                    </dt>
                    <dd class="col-sm-8 col-12 mb-0 text-muted">
                        <inline-editable-field
                            v-model="form.ticketLink"
                            :invalid-feedback="form.errors.ticketLink"
                            :line-height="22"
                            :placeholder="trans('workorder.form.ticket_link.placeholder')"
                            :resizable="false"
                        />
                    </dd>
                </div>
            </dl>
        </collapsable>

        <collapsable :is-collapsable="false" class="mb-7">
            <template #toggler>
                <h3 class="mb-3 text-xl !leading-[20px] !font-bold">
                    {{ trans('workorder.details.section.attachments.title') }}
                    <em v-if="attachmentsRequired" class="text-danger">*</em>
                </h3>
            </template>
            <file-upload
                v-model="form.attachments"
                :accepted-file-types="attachmentUiConfig.allowedFileTypes"
                :class="{ 'is-invalid': form.errors.attachments }"
                :drop-area-hint="attachmentUiConfig.dropAreaHint"
                :drop-area-label="attachmentUiConfig.dropAreaLabel"
                @upload-started="uploadInProgress = true"
                @upload-completed="uploadInProgress = false"
            />
            <div class="invalid-feedback">
                {{ form.errors.attachments }}
            </div>
        </collapsable>
    </Modal>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useForm } from '@inertiajs/vue3';
import AssetSelectionField from '@/Pages/WorkOrders/WorkOrderForm/AssetSelectionField.vue';
import InoperabilityReminderCheckbox from '@/Pages/WorkOrders/Modals/InoperabilityReminderCheckbox.vue';
import JetSelect from '@/Jetstream/Select.vue';
import FileUpload from '@/Components/FileUpload.vue';
import { scrollToFirstInvalidField } from '@/utils.js';
import AssetShortInfo from '@/Components/AssetShortInfo.vue';
import StatusBadge from '@/Components/StatusBadge.vue';
import { trans } from '@/mixins/i18n.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import WorkOrderTag from '@/Components/CustomTags/WorkOrderTag.vue';
import Collapsable from '@/Components/Collapsable.vue';
import InlineEditableField from '@/Pages/WorkOrders/Modals/InlineEditableField.vue';
import WorkOrderInfo from '@/Pages/WorkOrders/Modals/WorkOrderInfo.vue';
import ImpactAssessmentSection from '@/Pages/WorkOrders/Modals/ImpactAssessmentSection.vue';
import MsdNotificationSection from '@/Pages/WorkOrders/Modals/MsdNotificationSection.vue';
import CaseCategorySection from '@/Pages/WorkOrders/Modals/CaseCategorySection.vue';
import TroubleshootingSection from '@/Pages/WorkOrders/Modals/TroubleshootingSection.vue';
import { isImpactAssessmentComplete } from '@/Pages/WorkOrders/Modals/impactAssessment.js';
import { isCaseCategoryComplete, isMsdNotificationComplete, isTroubleshootingComplete } from '@/Pages/WorkOrders/Modals/catsaExtras.js';
import { useWorkOrderSectionGates } from '@/composables/useWorkOrderSectionGates';
import { useInoperableReminder } from '@/composables/useInoperableReminder';
import { eventBus, events } from '@/eventBus.js';

const props = defineProps({
    forcedWorkOrderType: {
        type: String,
        required: false,
    },
    showAssetSearch: {
        type: [Boolean, String],
        required: false,
    },
    assetId: {
        type: String,
        required: false,
    },
    asset: {
        type: Object,
        required: false,
    },
    priorities: {
        type: [Object, null],
        required: true,
    },
    types: {
        type: Object,
        required: true,
    },
    assignees: {
        type: [Array, null],
        required: true,
    },
    workOrderPropertyConfigByType: {
        type: Object,
        default: () => ({}),
    },
    derivedCaseCategory: {
        type: [String, null],
        default: null,
    },
    impactAssessment: { type: Object },
    catsaExtras: { type: Object },
    msdNotification: { type: Object },
    defaultContactPhone: { type: [String, null], default: null },
    caseCategories: { type: Array, default: () => [] },
    prefill: {
        type: Object,
        default: () => ({}),
    },
    prefilledAssigneeIdByType: {
        type: Object,
        default: () => ({}),
    },
    inoperableReminderKey: { type: [String, null], default: null },
});

const titleRef = ref(null);
const assetSelectionRef = ref(null);
const loadingAssetDetails = ref(false);
const modalRef = ref(null);
const uploadInProgress = ref(false);
const assignableGroups = computed(() => {
    return (props.assignees || []).map((assignee) => ({
        group: assignee.group,
        data: assignee.data.map((data) => ({
            label: data.name,
            value: data.id,
        })),
    }));
});

const pickPrefilledAssignee = (type, prefillMap = props.prefilledAssigneeIdByType) => prefillMap?.[type] ?? 'unassigned';

// Used only to pick the prefilled assignee: ignore a forced preventative type
// the user isn't allowed to create so we don't prefill for a type they can't pick.
const safeForcedWorkOrderType =
    props.forcedWorkOrderType === 'preventative_maintenance' && !hasAnyPermission('workorder.preventative.create')
        ? undefined
        : props.forcedWorkOrderType;

const form = useForm({
    type: 'corrective_maintenance',
    priority: 'medium',
    assignee: pickPrefilledAssignee(safeForcedWorkOrderType ?? 'corrective_maintenance'),
    startDate: null,
    dueDate: null,
    serviceDate: null,
    mspRespondedAt: null,
    title: null,
    description: null,
    ticketId: null,
    ticketLink: null,
    attachments: [],
    metadata: {
        impact_assessment: {},
        msd_notification: {},
        troubleshooting: {},
        contact_phone: props.defaultContactPhone ?? null,
        case_category: props.derivedCaseCategory ?? null,
        case_subcategory: null,
        inoperability_tag_acknowledged: false,
    },
});

const caseCategories = computed(() => props.caseCategories ?? []);

const priorityEnabled = hasFeature('work_order_priority');
const attachmentUiConfig = computed(() => props.workOrderPropertyConfigByType[form.type]?.attachments?.ui ?? {});
const attachmentsRequired = computed(() => attachmentUiConfig.value.required ?? false);

const { catsaExtrasEnabled, troubleshootingEnabled, msdNotificationEnabled, impactAssessmentEnabled } = useWorkOrderSectionGates({
    workOrderType: () => form.type,
    asset: () => props.asset,
    caseCategory: () => form.metadata.case_category ?? null,
    catsaExtras: props.catsaExtras,
    msdNotification: props.msdNotification,
    impactAssessment: props.impactAssessment,
});

const { visible: inoperableReminderVisible } = useInoperableReminder({
    reminderKey: () => props.inoperableReminderKey,
    workOrderType: () => form.type,
    caseCategory: () => form.metadata.case_category ?? null,
    catsaExtras: props.catsaExtras,
});

const isImpactAssessmentMissing = computed(() => impactAssessmentEnabled.value && !isImpactAssessmentComplete(form.metadata.impact_assessment));
const isMsdNotificationMissing = computed(() => msdNotificationEnabled.value && !isMsdNotificationComplete(form.metadata.msd_notification));
const isTroubleshootingMissing = computed(() => troubleshootingEnabled.value && !isTroubleshootingComplete(form.metadata.troubleshooting));
const isCaseCategoryMissing = computed(
    () => catsaExtrasEnabled.value && !isCaseCategoryComplete(form.metadata.case_category, form.metadata.case_subcategory),
);

const isRequiredPropertyMissing = computed(
    () =>
        (attachmentsRequired.value && !form.attachments?.length) ||
        isImpactAssessmentMissing.value ||
        isMsdNotificationMissing.value ||
        isTroubleshootingMissing.value ||
        isCaseCategoryMissing.value,
);

watch(
    () => form.type,
    (newType) => {
        if (!props.asset) {
            form.assignee = 'unassigned';
            return;
        }
        modalRef.value?.reload({
            data: { asset_id: props.asset.id, type: newType },
            onSuccess: () => {
                form.assignee = pickPrefilledAssignee(newType);
            },
        });
    },
);

watch(
    () => props.derivedCaseCategory,
    (newDefault) => {
        form.metadata.case_category = newDefault;
        form.metadata.case_subcategory = null;
    },
);

watch(
    () => form.assignee,
    (newAssignee) => {
        if (!props.asset) {
            return;
        }
        modalRef.value?.reload({
            only: ['workOrderPropertyConfigByType'],
            data: { asset_id: props.asset.id, type: form.type, assignee: newAssignee },
        });
    },
);

const onStartDateUpdate = (date) => {
    if (date > form.dueDate) {
        form.dueDate = null;
    }
};

const loadAssetDetails = (asset) => {
    loadingAssetDetails.value = true;
    form.processing = true;

    modalRef.value?.reload({
        data: { asset_id: asset.id, type: form.type },
        onSuccess: (response) => {
            // Read prefill map from the reload response — props is still stale inside this callback (PETC-2649).
            form.assignee = pickPrefilledAssignee(form.type, response?.data?.props?.prefilledAssigneeIdByType);
            loadingAssetDetails.value = false;
        },
        onFinish: () => {
            form.processing = false;
        },
    });
};

const submitForm = () => {
    if (!props.asset) {
        return;
    }

    form.transform((data) => {
        data.assetId = props.asset.id;

        if (!data.attachments) {
            delete data.attachments;
        }

        if (!priorityEnabled) {
            delete data.priority;
        }

        return data;
    }).post(route('workorders.store'), {
        onSuccess: () => {
            modalRef.value?.emit('created');
            eventBus.$emit(events.workOrderCreated);
            closeModal();
        },
        onError: () => {
            scrollToFirstInvalidField();
        },
    });
};

const closeModal = () => {
    modalRef.value?.close();
};

onMounted(() => {
    setTimeout(() => {
        if (assetSelectionRef.value) {
            assetSelectionRef.value.setActive(true);
        } else {
            titleRef.value?.setActive(true);
        }
    }, 300);
});
</script>
